/**
 * PD-16872 — the engines every AI agent runs on, chosen by the platform.
 *
 * Agents no longer carry their own voice model, listener or AI model: Workd
 * picks the best combination it has tested and every agent, system or
 * tenant-built, runs on it. Moving to a better model is a change here,
 * regression-tested once, and live for everyone. The per-agent columns on
 * `_conversation_agent` (voice_model, stt_*, llm_*) are no longer read or
 * written; sessions still record which engines actually ran.
 *
 * What an agent still chooses is what is genuinely its own: the voice (the
 * person you hear), its speaking direction, and a voice per language. Each
 * tenant also keeps a default voice and a voice per language (the
 * `ai_agents.voices` instance setting) for agents that name none; see
 * `resolveReplyVoiceId`.
 *
 * PD-16964: Workd associates (never tenant users) may give an agent its own
 * AI model for chat and for voice, in new columns (constants/agent-llm.ts);
 * the legacy `llm_*` columns stay unread.
 */

/**
 * The AI model behind every agent that has no model of its own (since
 * PD-16964 Workd may set one per agent and channel), and behind every AI
 * path not tied to an agent's conversation.
 *
 * PD-16986 (owner, 2026-10-12): Claude Haiku 5.5, chosen by the PD-14427
 * evaluation over gpt-5.4-mini (the default since 2026-10-07): more accurate
 * in chat and voice (judge pass 88-91% / 90-100% against 64-83%), faster to
 * the first word, and about a quarter of the cost per turn. Haiku bills a
 * whole request at about 5x above 100,000 prompt tokens, which is why the
 * Sales Helper's tool narrowing (PD-14463) matters on this model.
 */
export const PLATFORM_LLM = {
	provider: 'claude',
	model: 'claude-haiku-5-5',
} as const;

/**
 * The AI model every performance analyzer (and the post-call summary) runs
 * on. Owner, 2026-10-07: analyzers are done-for-you like agents, so nobody
 * picks the model per analyzer. Set to the same model as PLATFORM_LLM
 * (PD-16986: Claude Haiku 5.5); it may diverge after a comparison run on
 * scoring quality and cost.
 * The `llm_provider` / `llm_model` columns on `_conversation_analyzer` are
 * no longer read (new analyzers record this choice there); each analysis
 * result records the model that actually ran.
 */
export const PLATFORM_ANALYZER_LLM = {
	provider: 'claude',
	model: 'claude-haiku-5-5',
} as const;

/**
 * The text-to-speech engine. Eleven v4 Turbo starts speaking in ~150 ms and
 * reads written data well; ElevenLabs' own text normalization is on for every
 * request (it fixed abbreviations, ranges, units and Spanish and Mandarin
 * amounts in testing, at no extra cost and ~0.15 s later first audio).
 */
export const PLATFORM_VOICE = {
	provider: 'elevenlabs',
	model: 'eleven_v4_turbo',
	textNormalization: 'on',
} as const;

/**
 * The listener (speech-to-text). PD-12682: Deepgram Flux Multilingual runs the
 * conversation's turn-taking (it judges when a caller has finished better than
 * any other listener), and ElevenLabs Scribe v2 Realtime listens alongside at
 * the start of each call — and again whenever Flux's transcript looks wrong —
 * to catch languages Flux cannot hear, Chinese above all, and take the call
 * over. See crm-api stt-providers/multilingual-listener.ts.
 */
export const PLATFORM_LISTENER = {
	provider: 'multilingual',
	model: 'flux-general-multi',
} as const;

/**
 * The legacy engine columns a new `_conversation_agent` row is written with;
 * nothing reads them at runtime. An agent's own AI model (PD-16964) is in
 * `chatLlm*` / `voiceLlm*`, which a new agent leaves null (the platform
 * default).
 * Shared by every path that creates an agent (the editor, the assistant
 * wizard, a Workd template).
 */
export const PLATFORM_ENGINE_AGENT_COLUMNS = {
	llmProvider: PLATFORM_LLM.provider,
	llmModel: PLATFORM_LLM.model,
	voiceProvider: PLATFORM_VOICE.provider,
	voiceModel: PLATFORM_VOICE.model,
	sttProvider: PLATFORM_LISTENER.provider,
	sttModel: PLATFORM_LISTENER.model,
};

/**
 * A speaking direction: a short tone instruction ElevenLabs v3/v4 apply to
 * the voice ("casual", "calm", "speaking a bit slowly"). It is sent in square
 * brackets ahead of the reply's text and never appears in the chat bubble or
 * the transcript. These are the presets the agent editor offers; any short
 * custom wording is allowed too.
 */
export const SPEAKING_DIRECTION_PRESETS: ReadonlyArray<string> = [
	'casual',
	'calm',
	'upbeat',
	'professional',
	'warm',
];

/** Longest custom speaking direction accepted (it is spoken as a tag, not read). */
export const SPEAKING_DIRECTION_MAX_LENGTH = 80;

/** Sam, the platform's default voice (the Sales Helper's voice). */
export const PLATFORM_DEFAULT_VOICE_ID = 'rYW2LlWtM70M5vc3HBtm';

/**
 * The speaking direction for any voice whose profile stores none. Owner,
 * 2026-10-06: Sam sounds best "casual" on v4 ("friendly" and "conversational"
 * sounded performed, "calm" like a therapist). Owner, 2026-10-07, after
 * listening to v4 samples: a voice with no direction sounds too upbeat, so
 * "casual" became the default for every voice, not only Sam.
 */
export const PLATFORM_DEFAULT_SPEAKING_DIRECTION = 'casual';

/**
 * The voices an agent could speak with, as ElevenLabs voice ids. The language
 * lists are keyed by the language code the listener reports for a caller.
 */
export interface ReplyVoiceChoicesModel {
	/** The agent's own voice (its voice profile), when it has one. */
	agentVoiceId?: string | null;
	/** The agent's own voices for other languages. */
	agentLanguageVoiceIds?: Readonly<Record<string, string>> | null;
	/** The tenant's default voice, which is its English voice. */
	tenantDefaultVoiceId?: string | null;
	/** The tenant's "Voices by language" list. */
	tenantLanguageVoiceIds?: Readonly<Record<string, string>> | null;
}

/**
 * PD-12682 — the voice a reply is spoken in, for the caller's language.
 *
 * Owner decision 2026-10-07. For a language other than English: the agent's
 * own voice for that language (an agent may want, say, a female voice for one
 * language), then the tenant's voice for it, then the agent's own voice, then
 * the tenant's default voice, then Sam. For English, or before the caller's
 * language is known: the agent's own voice, then the tenant's default, then
 * Sam. Every step but Sam is visible and editable by the tenant: there is no
 * hidden per-language platform voice. Mandarin (James Gao) and Cantonese
 * (Ashley Y) start in each tenant's list instead, where they can be changed
 * or removed.
 */
export function resolveReplyVoiceId(
	language: string | null | undefined,
	choices: ReplyVoiceChoicesModel,
): string {
	const clean = (value: unknown): string | null =>
		typeof value === 'string' && value.trim() !== '' ? value : null;
	// Own keys only: the language code comes from the listener, and a code
	// such as "constructor" must not read an inherited property.
	const pick = (
		voices: Readonly<Record<string, string>> | null | undefined,
		code: string,
	): string | null =>
		voices != null && Object.prototype.hasOwnProperty.call(voices, code)
			? clean(voices[code])
			: null;
	const isOtherLanguage = language != null && language !== '' && language !== 'en';
	const languageVoice = isOtherLanguage
		? (pick(choices.agentLanguageVoiceIds, language) ??
			pick(choices.tenantLanguageVoiceIds, language))
		: null;
	return (
		languageVoice ??
		clean(choices.agentVoiceId) ??
		clean(choices.tenantDefaultVoiceId) ??
		PLATFORM_DEFAULT_VOICE_ID
	);
}

/**
 * Display names for the language codes the platform voice accepts, for the
 * voice settings screens. "zh" is spoken Mandarin and "yue" Cantonese (the
 * codes the listener reports). Listed here rather than taken from the
 * browser's `Intl.DisplayNames`, which calls "zh" "Chinese" and varies by
 * runtime. Kept in step with PLATFORM_VOICE_LANGUAGES by a unit test.
 */
export const PLATFORM_VOICE_LANGUAGE_NAMES: Readonly<Record<string, string>> = {
	af: 'Afrikaans',
	ar: 'Arabic',
	hy: 'Armenian',
	as: 'Assamese',
	ast: 'Asturian',
	az: 'Azerbaijani',
	be: 'Belarusian',
	bn: 'Bengali',
	bs: 'Bosnian',
	bg: 'Bulgarian',
	my: 'Burmese',
	yue: 'Cantonese',
	ca: 'Catalan',
	ceb: 'Cebuano',
	ny: 'Chichewa',
	hr: 'Croatian',
	cs: 'Czech',
	da: 'Danish',
	nl: 'Dutch',
	en: 'English',
	et: 'Estonian',
	fil: 'Filipino',
	fi: 'Finnish',
	fr: 'French',
	gl: 'Galician',
	ka: 'Georgian',
	de: 'German',
	el: 'Greek',
	gu: 'Gujarati',
	ha: 'Hausa',
	he: 'Hebrew',
	hi: 'Hindi',
	hu: 'Hungarian',
	is: 'Icelandic',
	id: 'Indonesian',
	ga: 'Irish',
	it: 'Italian',
	ja: 'Japanese',
	jv: 'Javanese',
	kn: 'Kannada',
	kk: 'Kazakh',
	ky: 'Kyrgyz',
	ko: 'Korean',
	lv: 'Latvian',
	ln: 'Lingala',
	lt: 'Lithuanian',
	lb: 'Luxembourgish',
	mk: 'Macedonian',
	ms: 'Malay',
	ml: 'Malayalam',
	mt: 'Maltese',
	zh: 'Mandarin',
	mi: 'Maori',
	mr: 'Marathi',
	mn: 'Mongolian',
	ne: 'Nepali',
	no: 'Norwegian',
	oc: 'Occitan',
	or: 'Odia',
	ps: 'Pashto',
	fa: 'Persian',
	pl: 'Polish',
	pt: 'Portuguese',
	pa: 'Punjabi',
	ro: 'Romanian',
	ru: 'Russian',
	sr: 'Serbian',
	sd: 'Sindhi',
	sk: 'Slovak',
	sl: 'Slovenian',
	so: 'Somali',
	es: 'Spanish',
	sw: 'Swahili',
	sv: 'Swedish',
	tg: 'Tajik',
	ta: 'Tamil',
	te: 'Telugu',
	th: 'Thai',
	tr: 'Turkish',
	uk: 'Ukrainian',
	ur: 'Urdu',
	uz: 'Uzbek',
	vi: 'Vietnamese',
	cy: 'Welsh',
	yo: 'Yoruba',
};

/**
 * Language codes Eleven v4 Turbo accepts (ElevenLabs /v1/models, 2026-10-07).
 * Any other code makes it refuse the reply ("unsupported_language"), so a
 * code outside this list is never sent; the voice then follows the reply's
 * text on its own.
 */
export const PLATFORM_VOICE_LANGUAGES: ReadonlySet<string> = new Set([
	'af', 'ar', 'hy', 'as', 'ast', 'az', 'be', 'bn', 'bs', 'bg', 'my', 'yue', 'ca', 'ceb', 'ny', 'hr', 'cs',
	'da', 'nl', 'en', 'et', 'fil', 'fi', 'fr', 'gl', 'ka', 'de', 'el', 'gu', 'ha', 'he', 'hi', 'hu', 'is',
	'id', 'ga', 'it', 'ja', 'jv', 'kn', 'kk', 'ky', 'ko', 'lv', 'ln', 'lt', 'lb', 'mk', 'ms', 'ml', 'mt',
	'zh', 'mi', 'mr', 'mn', 'ne', 'no', 'oc', 'or', 'ps', 'fa', 'pl', 'pt', 'pa', 'ro', 'ru', 'sr', 'sd',
	'sk', 'sl', 'so', 'es', 'sw', 'sv', 'tg', 'ta', 'te', 'th', 'tr', 'uk', 'ur', 'uz', 'vi', 'cy', 'yo',
]);

/**
 * The speaking direction for a reply: the agent's own direction (one override
 * for all its voices), else the stored direction of the voice that is
 * speaking, else the platform default ("casual"). The default lives on the
 * voice (owner, 2026-10-07), so `voiceProfileDirection` must be the profile of
 * the voice actually speaking, which for a Mandarin reply may be James Gao
 * rather than the agent's main voice.
 */
export function resolveSpeakingDirection(
	agentDirection: string | null | undefined,
	voiceProfileDirection: string | null | undefined,
): string {
	const clean = (value: string | null | undefined): string | null => {
		if (value == null) {
			return null;
		}
		// Brackets would break the tag the direction is sent in.
		const trimmed = value.replace(/[[\]]/g, '').trim().slice(0, SPEAKING_DIRECTION_MAX_LENGTH);
		return trimmed === '' ? null : trimmed;
	};
	return clean(agentDirection) ?? clean(voiceProfileDirection) ?? PLATFORM_DEFAULT_SPEAKING_DIRECTION;
}

/** What a reply is spoken with: the voice and its speaking direction. */
export interface ReplyVoiceModel {
	voiceId: string;
	speakingDirection: string;
}

/**
 * PD-12682 / PD-16872 — the voice for a reply in `language`
 * (`resolveReplyVoiceId`) and the speaking direction that goes with it
 * (`resolveSpeakingDirection`, using the stored direction of that voice).
 * `voiceDirections` maps ElevenLabs voice id to its profile's stored
 * direction, for every voice the agent could speak with.
 */
export function resolveReplyVoice(
	language: string | null | undefined,
	choices: ReplyVoiceChoicesModel & {
		agentSpeakingDirection?: string | null;
		voiceDirections?: Readonly<Record<string, string | null>> | null;
	},
): ReplyVoiceModel {
	const voiceId = resolveReplyVoiceId(language, choices);
	const directions = choices.voiceDirections;
	const voiceDirection =
		directions != null && Object.prototype.hasOwnProperty.call(directions, voiceId)
			? directions[voiceId]
			: null;
	return {
		voiceId,
		speakingDirection: resolveSpeakingDirection(choices.agentSpeakingDirection, voiceDirection),
	};
}
